import Data, { treeNode } from '@/data'
import { PassiveSkillDefinition, Range } from '@/data/types'
import { WithPassiveTooltip } from '@/tooltip'
import Select, { SelectOption, underlinerFunc } from '@/ui/select'
import React, { MouseEvent, useCallback } from 'react'

type PassiveOption = SelectOption<number>

const renderPassive = ({ id, name }: PassiveOption, searchRe?: RegExp) => (
  <WithPassiveTooltip id={id}>
    <span>{underlinerFunc(name, searchRe)}</span>
  </WithPassiveTooltip>
)

function filterPassive(skill: PassiveSkillDefinition, id: number, range?: Range) {
  if (!range) {
    return skill.is_notable && !skill.ascendancy
  } else if (range.max > 0) {
    return id >= range.min && id <= range.max
  } else if (range.max === -1) {
    // keystone
    return skill.is_keystone
  } else if (range.max === -2) {
    // ascendancy notable
    return skill.is_notable && skill.ascendancy != null
  }
}

export function PassiveSelector({
  value,
  onChange,
  disabled,
  range
}: {
  value: number
  onChange: (value: number) => void
  disabled?: boolean
  range?: Range
}) {
  const stopPropagation = useCallback((event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation()
  }, [])
  const getOptions = React.useCallback(() => {
    const options: PassiveOption[] = []
    for (const [id, node] of Object.entries(Data.passive_tree.nodes)) {
      const skill = 'skill_id' in node ? Data.passive_skills[node.skill_id] : node
      if (filterPassive(skill, Number(id), range)) {
        options.push({ id: Number(id), name: skill.name })
      }
    }
    return options.sort((a, b) => a.name.localeCompare(b.name))
  }, [range])
  const onSelectValue = React.useCallback((opt?: PassiveOption) => onChange(opt?.id ?? 0), [onChange])
  const node = treeNode(value)
  if (disabled || (range && range.min === range.max && range.min > 0 && value === range.min)) {
    return <span>{node ? node.name : '<Passive>'}</span>
  }

  return (
    <Select
      options={getOptions}
      value={node ? { id: value, name: node.name } : undefined}
      onChange={onSelectValue}
      onClick={stopPropagation}
      placeholder="Select Passive..."
      searchLimit={10}
      searchMiddle
      renderOption={renderPassive}
      renderValue={renderPassive}
    />
  )
}
